import allocate from "../util/allocate";
import Vector from "../util/vector";

/** Amount of force with which a point is either attracted or repulsed by the invisible center */
const POINT_CENTER_FORCE = 0.001;

/** Amount of force with which the points repulse each other */
const POINT_FORCE = 0.0005;

const POINT_CROSS_FORCE = 0.0004;

const DRAG_FORCE = 0.01;

/** Amount of force with which invisible center is attracted to the sine-based center target */
const CENTER_TARGET_ATTRACTION_FORCE = 0.005;

/** controls the loss of impulse for our points. lower friction leads to longer, more springy movement */
const FRICTION = 0.03;

/**
 * Maximum distance for which a point will be considered as being close to the mouse.
 */
const MOUSE_DISTANCE = 64;

/**
 * Number physically simulated circle points forming a regular inscribed polygon. Must be a multiple of 3.
 *
 * Each has a repulsive spring connection to its neighbor points and both a repulsive and attractive connection
 * to a invisible center point
 *
 * Also we are connecting the points across the circle in a triangle formation where every dot is connected to the
 * dots a third of the circle to the left and to the right of it. (hence the multiple of 3 requirement)
 *
 */
export const CIRCLE_RESOLUTION = 9;

/** How much mass does the invisible center point have? ( circle points have a mass of 1) */
const CENTER_MASS = 1;

/** how large is the sine-movement of the ideal center target points compared to the circle size? */
const MOVEMENT_FACTOR = 0.07;

/** distance of the control points in relation to the radius size */
const CONTROL_SCALE = 0.25;

/** speed multiplier for speed table below */
const SPEED_FACTOR  = 0.004;

/** Speeds picked at random for the sine-based center target position */
const SPEEDS = [
        SPEED_FACTOR,
    3 * SPEED_FACTOR,
    5 * SPEED_FACTOR,
    7 * SPEED_FACTOR,
    11 * SPEED_FACTOR,
    13 * SPEED_FACTOR,
    17 * SPEED_FACTOR,
];

////////////////////////////////////////////////// INTERNAL CONSTANTS //////////////////////////////////////////////////

const TAU = Math.PI * 2;

// Array elements per simulation point: x,y, dx,dy, mass
const ELEMENTS = 5;

/** <ELEMENTS> float values per circle point */
const CIRCLE_POINT_LEN = CIRCLE_RESOLUTION * ELEMENTS;
/** Array offset for the center target point which is the point the center follows */
const CENTER_TARGET_OFFSET = CIRCLE_POINT_LEN;
/** current mouse pos offset */
const MOUSE_POS_OFFSET = CIRCLE_POINT_LEN + ELEMENTS;

const AVERAGE_POS_OFFSET = CIRCLE_POINT_LEN + 2 * ELEMENTS;

// .. plus the invisible center point plus center target plus mouse pos
const DATA_LEN = CIRCLE_POINT_LEN + 3 * ELEMENTS;

const sqrt = Math.sqrt;

function randomSpeed()
{
    return SPEEDS[(Math.random() * SPEEDS.length)|0];
}

/**
 * Returns the edge size of a n-sided regular inscribed polygon inside a circle of the given radius.
 *
 * @param n             number of sides
 * @param radius        radius
 * @returns {number}
 */
function getEdgeLength(n, radius)
{
    return 2 * radius * Math.sin(TAU / n / 2);
}

let vControl1 = new Vector(0,0);
let vControl2 = new Vector(0,0);

class Circle
{
    constructor(ctx, x, y, size, config)
    {
        this.ctx = ctx;
        this.x = x;
        this.y = y;
        this.size = size;
        this.color = config.color;
        this.lineWidth = config.lineWidth;
        this.minDistanceIndex = -1;

        this.isDragged = false;
        this.dragOffsetX = 0;
        this.dragOffsetY = 0;

        this.ax0 = 0;//Math.random() * TAU;
        this.ay0 = 0;//Math.random() * TAU;
        this.ax1 = 0;//Math.random() * TAU;
        this.ay1 = 0;//Math.random() * TAU;

        this.dx0 = randomSpeed();
        this.dy0 = randomSpeed();
        this.dx1 = randomSpeed();
        this.dy1 = randomSpeed();

        this.controlPointDistance = CONTROL_SCALE * size/2;

        const angleStep = TAU / CIRCLE_RESOLUTION;

        const data = allocate(DATA_LEN);
        this.data = data;
        const radius = size * 0.5;

        for (let i = 0, a = 0; i < CIRCLE_POINT_LEN ; i += ELEMENTS, a += angleStep)
        {
            // init all circle points with position in <size> distance and zero velocity
            data[i    ] = this.x + Math.cos(a) * radius;
            data[i + 1] = this.y + Math.sin(a) * radius;
            data[i + 2] = 0;
            data[i + 3] = 0;
            data[i + 4] = 1;
        }
    }

    updateCenter()
    {
        const offset = this.size * MOVEMENT_FACTOR;
        const smallOffset = offset * 0.61803398875;


        this.data[CENTER_TARGET_OFFSET    ] = this.x + Math.cos(this.ax0) * offset + Math.cos(this.ax1) * smallOffset;
        this.data[CENTER_TARGET_OFFSET + 1] = this.y + Math.sin(this.ay0) * offset + Math.sin(this.ay1) * smallOffset;

        this.ax0 += this.dx0;
        this.ay0 += this.dy0;
        this.ax1 -= this.dx1;
        this.ay1 -= this.dy1;
    }

    render(stats)
    {
        const { ctx , data, minDistanceIndex} = this;

        ctx.lineWidth = this.lineWidth;
        ctx.strokeStyle = this.color;

        let x0 = data[0];
        let y0 = data[1];

        ctx.save();
        ctx.beginPath();
        ctx.globalAlpha = 0.6;
        ctx.moveTo(x0, y0);

        for (let i = ELEMENTS; i < CIRCLE_POINT_LEN ; i += ELEMENTS)
        {
            let x1 = data[i];
            let y1 = data[i + 1];

            this.drawBezier( x0, y0, x1, y1, stats);
            x0 = x1;
            y0 = y1;
        }

        this.drawBezier(x0, y0, data[0], data[1], stats);
        ctx.stroke();
        ctx.restore();

        if (minDistanceIndex > 0)
        {
            let x = data[minDistanceIndex    ];
            let y = data[minDistanceIndex + 1];
            
            ctx.strokeStyle = "rgba(255,255,255,0.66)";
            ctx.beginPath();
            ctx.arc(x, y, 5, 0, TAU, false);
            ctx.stroke();
        }

        if (process.env.NODE_ENV !== "production")
        {
            if (stats.cpLine)
            {
                const { data } = this;

                const centerTargetX = data[CENTER_TARGET_OFFSET    ];
                const centerTargetY = data[CENTER_TARGET_OFFSET + 1];
                const averageX = data[AVERAGE_POS_OFFSET    ];
                const averageY = data[AVERAGE_POS_OFFSET + 1];

                ctx.fillStyle = "#f00";
                ctx.fillRect(centerTargetX, centerTargetY, 2, 2);

                ctx.fillStyle = "#0f0";
                ctx.fillRect( averageX, averageY, 2, 2);
            }
        }
    }

    drawBezier(x1, y1, x2, y2, stats)
    {
        const centerX = this.data[AVERAGE_POS_OFFSET    ];
        const centerY = this.data[AVERAGE_POS_OFFSET + 1];

        vControl1.x = centerX - x1;
        vControl1.y = centerY - y1;

        vControl2.x = centerX - x2;
        vControl2.y = centerY - y2;

        vControl1.rotateClockwise().norm(this.controlPointDistance).add(x1,y1);
        vControl2.rotateCounterClockwise().norm(this.controlPointDistance).add(x2,y2);

        const { ctx } = this;

        if (process.env.NODE_ENV !== "production")
        {
            if (stats.cpLine)
            {
                // if we have a buffer we collect the debug lines between circle points and control points
                let pos = stats.cpPos;
                stats.cpLine[pos++] = x1;
                stats.cpLine[pos++] = y1;
                stats.cpLine[pos++] = vControl1.x;
                stats.cpLine[pos++] = vControl1.y;
                stats.cpLine[pos++] = vControl2.x;
                stats.cpLine[pos++] = vControl2.y;
                stats.cpLine[pos++] = x2;
                stats.cpLine[pos++] = y2;
                stats.cpPos = pos;

            }
        }

        // .. and extend the current AABB by the control points
        stats.aabb.extend(vControl1.x, vControl1.y);
        stats.aabb.extend(vControl2.x, vControl2.y);

        ctx.bezierCurveTo(vControl1.x, vControl1.y, vControl2.x, vControl2.y, x2, y2);
    }

    update(stats)
    {
        this.updateCenter();

        const { data, size, minDistanceIndex} = this;
        const { aabb } = stats;

        const centerTargetX = data[CENTER_TARGET_OFFSET    ];
        const centerTargetY = data[CENTER_TARGET_OFFSET + 1];

        //aabb.extend(centerTargetX,centerTargetY);


        // springs at the edges of the regular polygon between circle points.

        let avgX = data[0];
        let avgY = data[1];

        const radius = size/2;
        const maxPointDistance = getEdgeLength(CIRCLE_POINT_LEN, radius);
        for (let i = ELEMENTS; i < CIRCLE_POINT_LEN ; i += ELEMENTS)
        {
            this.attract(i, i - ELEMENTS, maxPointDistance, POINT_FORCE);
            this.repulse(i, i - ELEMENTS, maxPointDistance, POINT_FORCE);

            avgX += data[i];
            avgY += data[i + 1];
        }
        this.attract(0, CIRCLE_POINT_LEN - ELEMENTS, maxPointDistance, POINT_FORCE);
        this.repulse(0, CIRCLE_POINT_LEN - ELEMENTS, maxPointDistance, POINT_FORCE);

        avgX /= CIRCLE_RESOLUTION;
        avgY /= CIRCLE_RESOLUTION;

        data[AVERAGE_POS_OFFSET    ] = avgX;
        data[AVERAGE_POS_OFFSET + 1] = avgY;

        const thirdLength = CIRCLE_POINT_LEN/3;
        const thirdDist = getEdgeLength(3, radius);

        for (let i = thirdLength; i < CIRCLE_POINT_LEN - thirdLength; i += ELEMENTS)
        {
            // current point -> third left
            this.attract(              i, i - thirdLength, thirdDist, POINT_CROSS_FORCE);
            this.repulse(              i, i - thirdLength, thirdDist, POINT_CROSS_FORCE);
            // current point -> third right
            this.attract(              i, i + thirdLength, thirdDist, POINT_CROSS_FORCE);
            this.repulse(              i, i + thirdLength, thirdDist, POINT_CROSS_FORCE);
            // between the other two points
            this.attract(i - thirdLength, i + thirdLength, thirdDist, POINT_CROSS_FORCE);
            this.repulse(i - thirdLength, i + thirdLength, thirdDist, POINT_CROSS_FORCE);
        }

        for (let i = 0; i < CIRCLE_POINT_LEN ; i += ELEMENTS)
        {
            this.attract(i, AVERAGE_POS_OFFSET, radius, POINT_CENTER_FORCE);
            this.repulse(i, AVERAGE_POS_OFFSET, radius, POINT_CENTER_FORCE);

            this.attract(i, CENTER_TARGET_OFFSET, radius, CENTER_TARGET_ATTRACTION_FORCE);
        }

        if (this.isDragged && minDistanceIndex > 0)
        {
            this.attract(minDistanceIndex, MOUSE_POS_OFFSET, 0, DRAG_FORCE);
        }

        // apply deltas for all circle points
        for (let i = 0; i < CIRCLE_POINT_LEN ; i += ELEMENTS)
        {
            let x = data[i    ];
            let y = data[i + 1];

            x += data[i + 2];
            y += data[i + 3];

            data[i    ] = x;
            data[i + 1] = y;
            data[i + 2] *= (1 - FRICTION);
            data[i + 3] *= (1 - FRICTION);

            aabb.extend(x,y);
        }

    }

    repulse(off0, off1, distance, force)
    {
        const { data } = this;

        const x0 = data[off0    ];
        const y0 = data[off0 + 1];

        const x = data[off1    ] - x0;
        const y = data[off1 + 1] - y0;

        const d = sqrt( x * x + y * y);

        const mass0 = data[off0 + 4];
        const mass1 = data[off1 + 4];

        if (d < distance && d > 0)
        {
            // scale force proportional to the current distance
            const f = force * sqrt(d);

            // scale target vector to force
            const sx = x * f / d;
            const sy = y * f / d;

            data[off0 + 2] -= sx / mass0;
            data[off0 + 3] -= sy / mass0;

            data[off1 + 2] += sx / mass1;
            data[off1 + 3] += sy / mass1;

        }
    }

    attract(off0, off1, distance, force)
    {
        const { data } = this;

        const x0 = data[off0    ];
        const y0 = data[off0 + 1];
        const x1 = data[off1    ];
        const y1 = data[off1 + 1];


        const mass0 = data[off0 + 4];
        const mass1 = data[off1 + 4];

        const x = x1 - x0;
        const y = y1 - y0;

        const d = sqrt( x * x + y * y);
        if (d > distance)
        {
            const f = force * sqrt( d - distance);

            // scale target vector to distance beyond limit
            const sx = x * f / d;
            const sy = y * f / d;

            data[off0 + 2] += sx / mass0;
            data[off0 + 3] += sy / mass0;

            data[off1 + 2] -= sx / mass1;
            data[off1 + 3] -= sy / mass1;
        }
    }

    updateMouse(x,y)
    {
        const { data, isDragged } = this;

        data[MOUSE_POS_OFFSET] = x;
        data[MOUSE_POS_OFFSET + 1] = y;

        if (!isDragged)
        {

            let minDistance = Infinity;
            let minDistanceIndex = -1;

            for (let i = 0; i < CIRCLE_POINT_LEN ; i += ELEMENTS)
            {
                let dx = x - data[i    ];
                let dy = y - data[i + 1];

                if (Math.abs(dx) < MOUSE_DISTANCE && Math.abs(dy) < MOUSE_DISTANCE)
                {
                    const d = sqrt( dx * dx + dy * dy);
                    if (d < minDistance)
                    {
                        minDistance = d;
                        minDistanceIndex = i;
                    }
                }
            }

            this.minDistanceIndex = minDistanceIndex;
        }
    }
}

export default Circle



// WEBPACK FOOTER //
// ./src/circles/circle.js